import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_ISSUES_AND_UPGRADES_VIEW } from '@/ability';
import {
    getIssueOrUpgradeApiId,
    getIssueOrUpgradeMarkdown,
    getIssueOrUpgradeType,
    getIssueUpgradeLinkedTasks,
} from '@hooks/projects/useProjectIssuesAndUpgrades';
import {
    getAttachmentFileExtension,
    isLikelyFileAttachmentUrl,
    isLikelyImageAttachmentUrl,
    normalizePhaseLinkUrl,
    resolveAttachmentUrl,
} from '@hooks/projects/projectHelpers';
import { getProjectIssueOrUpgradeTasks } from '@services/projects.service';

function getIssueUpgradeAttachmentItemsForDetail(item) {
    const list = [
        ...(Array.isArray(item?.attachments) ? item.attachments : []),
        ...(Array.isArray(item?.attachmentUrls) ? item.attachmentUrls : []),
        ...(Array.isArray(item?.files)
            ? item.files
                  .map((f) => ({
                      type: 'file',
                      url: f?.url || f?.downloadUrl || f?.path || '',
                      title: f?.name || f?.filename || 'file',
                  }))
                  .filter((x) => x.url)
            : []),
    ];
    return list.filter(Boolean);
}

export function useIssueUpgradeDetailModal(projectId, item) {
    const can = useCanCheck();
    const canView = can(PROJECT_ISSUES_AND_UPGRADES_VIEW);
    const issueId = getIssueOrUpgradeApiId(item);
    const open = Boolean(item && issueId);
    const [detailTab, setDetailTab] = useState('attachments');
    const [descriptionPreviewImage, setDescriptionPreviewImage] = useState('');

    const descriptionMarkdown = useMemo(() => (item ? getIssueOrUpgradeMarkdown(item) : ''), [item]);
    const hasDescription = Boolean(String(descriptionMarkdown || '').trim());

    const typeLabelRaw = item ? getIssueOrUpgradeType(item) : 'issue';
    const taskTypeLabel = typeLabelRaw === 'upgrade' ? 'Upgrade' : 'Issue';

    const rawAttachments = useMemo(() => (item ? getIssueUpgradeAttachmentItemsForDetail(item) : []), [item]);

    const attachmentLinks = useMemo(
        () =>
            rawAttachments.filter((x) => {
                const typ = String(x?.type ?? '').toLowerCase();
                const url = String(x?.url || '').trim();
                return typ === 'link' || !isLikelyFileAttachmentUrl(url);
            }),
        [rawAttachments],
    );

    const attachmentFiles = useMemo(
        () =>
            rawAttachments.filter((x) => {
                const typ = String(x?.type ?? '').toLowerCase();
                const url = String(x?.url || '').trim();
                return url && (typ !== 'link' || isLikelyFileAttachmentUrl(url));
            }),
        [rawAttachments],
    );

    const attachmentCount = rawAttachments.length;

    const descriptionAttachmentItems = useMemo(
        () =>
            rawAttachments.map((x) => ({
                type: String(x?.type || '').toLowerCase() === 'link' ? 'link' : 'file',
                url: x?.url,
                title: x?.title ?? x?.url,
            })),
        [rawAttachments],
    );

    const attachmentFilesPresentation = useMemo(
        () =>
            attachmentFiles.map((att, idx) => {
                const rawUrl = String(att?.url || '').trim();
                const href = resolveAttachmentUrl(rawUrl);
                const isImage = isLikelyImageAttachmentUrl(rawUrl);
                const ext = getAttachmentFileExtension(rawUrl);
                return { item: { id: `${rawUrl}-${idx}`, name: att?.title || rawUrl || 'Attachment' }, href, isImage, ext };
            }),
        [attachmentFiles],
    );

    const attachmentLinksPresentation = useMemo(
        () =>
            attachmentLinks.map((l, idx) => {
                const href = normalizePhaseLinkUrl(l?.url || '');
                const title = String(l?.title || href || '').trim() || href || 'Link';
                return { id: `link-${idx}`, href, title };
            }),
        [attachmentLinks],
    );

    const embeddedTasksFallback = useMemo(() => getIssueUpgradeLinkedTasks(item), [item]);

    const {
        data: fetchedTasks = [],
        isLoading: tasksLoading,
    } = useQuery({
        queryKey: ['projects', projectId, 'issues-and-upgrades', issueId, 'tasks'],
        queryFn: () => getProjectIssueOrUpgradeTasks(projectId, issueId),
        enabled: Boolean(projectId && issueId && open && canView),
        staleTime: 60_000,
        retry: 0,
    });

    const tasksList = useMemo(() => {
        if (Array.isArray(fetchedTasks) && fetchedTasks.length > 0) return fetchedTasks;
        return embeddedTasksFallback;
    }, [fetchedTasks, embeddedTasksFallback]);

    /** Avoid blanking embedded tasks while the tasks query is still loading. */
    const tasksShowLoading = Boolean(tasksLoading && (tasksList?.length ?? 0) === 0);

    const resolveAttachmentHrefFromItems = useCallback((hrefStr, items) => {
        if (!hrefStr) return '';
        const m = /^#attachment-(\d+)$/i.exec(hrefStr);
        if (!m) return '';
        const idx = Number(m[1]);
        if (Number.isNaN(idx) || idx < 0) return '';
        const source = Array.isArray(items) ? items : [];
        const filesOnly = source.filter((x) => String(x?.type || '').toLowerCase() !== 'link');
        const target = filesOnly[idx] ?? source[idx];
        return target?.url ? resolveAttachmentUrl(target.url) : '';
    }, []);

    const handleMarkdownImageLinkClick = useCallback(
        ({ event, href, rawHref, isImage }, items) => {
            if (!href && !rawHref) return;
            const raw = String(rawHref || '');
            if (/^#attachment-\d+$/i.test(raw)) {
                const resolved = resolveAttachmentHrefFromItems(raw, items);
                if (!resolved) return;
                event?.preventDefault?.();
                event?.stopPropagation?.();
                if (isLikelyImageAttachmentUrl(resolved)) {
                    setDescriptionPreviewImage(resolved);
                    return;
                }
                window.open(resolved, '_blank', 'noopener,noreferrer');
                return;
            }
            const h = href || '';
            if (isImage || isLikelyImageAttachmentUrl(h)) {
                event?.preventDefault?.();
                event?.stopPropagation?.();
                setDescriptionPreviewImage(h);
            }
        },
        [resolveAttachmentHrefFromItems],
    );

    useEffect(() => {
        if (open) {
            setDetailTab('attachments');
            setDescriptionPreviewImage('');
        }
    }, [open, issueId]);

    const reasonMarkdown = String(item?.reason ?? '').trim();

    return {
        detailTab,
        setDetailTab,
        descriptionPreviewImage,
        setDescriptionPreviewImage,
        issueId,
        open,
        taskTypeLabel,
        hasDescription,
        descriptionMarkdown,
        descriptionAttachmentItems,
        attachmentCount,
        attachmentLinksPresentation,
        attachmentFilesPresentation,
        handleMarkdownImageLinkClick,
        reasonMarkdown,
        tasksList,
        tasksLoading,
        tasksShowLoading,
    };
}
